import { Button, GoalsIllustration, Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import React from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { AppointmentsAvatar, FEATURE_AVATAR_SIZE } from "./AppointmentsAvatar"
import { formatProviderName } from "./formatting"
import { DESKTOP_BREAKPOINT, perViewport, perViewportTypography } from "./responsive"
import type { Provider } from "./types"

/** `.empty-avs` draws exactly three faces. A longer list is truncated rather than wrapped. */
const MAX_SPECIALIST_FACES = 3

/**
 * `.empty-body { max-width: 380px }` — a content measure, not a breakpoint. It keeps the copy clear
 * of the illustration on the tile's right edge and, being a cap, needs no responsive variant.
 *
 * Deliberately NOT relaxed for frame 9, though `.mob-ep` is unconstrained. At 390px the tile is about
 * 358px wide (`PageLayout` insets the content 16px a side below `md`) less its own padding, so the cap
 * never binds and the copy already runs the full width — while raising it would only let the text
 * slide under the illustration, which the tile draws behind the content rather than beside it.
 */
const BODY_MAX_INLINE_SIZE = 380

/**
 * The booking card — frame 1 leading an empty main column, and frames 2–5 and 8 sitting alongside an
 * appointment. One component for all of them: the card itself never varies, only where the page puts
 * it, which is CONAPP-270's call.
 *
 * `Tile variant="primary" color="primary"` paints `palette.gradients.primary`, which is the design's
 * `.empty-card` gradient, and `GoalsIllustration` in the `illustration` slot *is* the `.empty-illo`
 * shape grid — so neither needs an `sx` override and no new asset is required.
 *
 * The faces are page data (`AppointmentsPageData.specialists`), not a constant in here: who a member
 * could book with is a server answer, and every fixture already supplies three.
 */
export function BookAppointmentCard({
  specialists,
  bookingPath,
  onBookAppointment
}: {
  /**
   * Nullable on purpose. The list is fixture data today and API data later, and this card would
   * rather draw its copy and CTA with no faces than take the page down — so the tolerance is in the
   * type rather than left to the host's `strictNullChecks` being off, which is what carried it before
   * the move (see `AppointmentsPage.test.tsx`, "survives page data with nothing in it").
   */
  specialists: Provider[] | null | undefined
  /** The booking route, injected by the host — the package cannot reach its `Paths` enum. */
  bookingPath: string
  onBookAppointment?: React.MouseEventHandler<HTMLElement>
}): JSX.Element {
  const theme = useTheme()
  // Defaulting rather than guarding keeps the copy and the CTA on screen when only the faces absent.
  const faces = (specialists ?? []).slice(0, MAX_SPECIALIST_FACES)

  return (
    <Tile
      variant="primary"
      color="primary"
      illustration={<GoalsIllustration />}
      /*
        Frame 9's `.mob-empty` draws no illustration at all, so it is dropped below the breakpoint
        rather than left to crowd 310px of copy.
        `Tile` already thins it out on its own — the illustration container is `display: none` under a
        `@container (min-width: 350px)` query and, between 350px and 550px, `translateX(50%)` pushes
        half of it outside the tile's `overflow: clip`. At 320px that container query alone hides it.
        At 390px the container measures ~358px, so it reappears half-off-canvas *behind* the copy
        (`& > *:not([data-tile-illustration])` is `position: relative`), which is the crowding this
        ticket asks about.
        Scoped to a `max-width` query so it only ADDS a rule below `md` and leaves Carotene's
        container-query behaviour untouched at every width the desktop composition uses — overriding
        a design-system container query with a media query across the board would be fighting it.
      */
      slotProps={{
        illustrationContainer: { sx: { [theme.breakpoints.down(DESKTOP_BREAKPOINT)]: { display: "none" } } }
      }}
    >
      <Stack
        sx={{
          flexDirection: "column",
          // `.mob-btn { width: 100% }` — stretching the column is what widens the CTA, since `Button`
          // is `inline-flex` and takes the cross-axis default. Harmless for the other children: the
          // faces row keeps `justify-content: flex-start` and the copy is block-level already.
          alignItems: perViewport("stretch", "flex-start"),
          maxInlineSize: BODY_MAX_INLINE_SIZE
        }}
        spacing={theme.spacing(theme.tokens.spacing.md)}
      >
        {/*
          `aria-hidden` because the design writes these as `alt=""` — decorative faces with no name
          beside them. The distinction matters here and not in the appointment rows: every fixture
          specialist ships `photoUrl: null`, so the initials fallback is the *default* path, and it
          renders text ("PR", "AO", "SP") that a screen reader would otherwise read out as three
          meaningless pairs of letters. Nothing inside is interactive.

          Provisional default pending design: initials, no image files. See CONAPP-269.
        */}
        {faces.length > 0 && (
          <Stack aria-hidden="true" sx={{ flexDirection: "row" }} spacing={theme.spacing(theme.tokens.spacing.xs)}>
            {faces.map((provider) => (
              <AppointmentsAvatar
                key={provider.name}
                name={provider.name}
                photoUrl={provider.photoUrl}
                alt={formatProviderName(provider)}
                size={FEATURE_AVATAR_SIZE}
              />
            ))}
          </Stack>
        )}
        {/* `.empty-h` carries `--h6-*` type, so `h6` rather than the `h3` the section headings use. */}
        <Typography variant="h6" component="h2">
          Get care from a Carrot clinician
        </Typography>
        {/* `body1` is already `text.secondary` in the Carotene theme, matching `.empty-p`. */}
        {/* `.empty-p` → `.mob-ep`, body1 → body2 at frame 9's width. */}
        <Typography variant="body1" sx={{ typography: perViewportTypography("body2", "body1") }}>
          Book a virtual appointment with a Carrot clinician to talk through your questions and get guidance on what to
          do next.
        </Typography>
        {/*
          A real in-app route, so a router link rather than an anchor — the same wiring the near
          identical tile in `ConsumerCarrotPlanSidebar` uses. `/telemedicine/booking` is gated by the
          `telemedicineBookingFlow` flag and redirects home while it is off; that is the route's
          business, not this card's.
        */}
        <Button
          variant="outlined"
          color="secondary"
          component={ReactRouterLink}
          to={bookingPath}
          onClick={onBookAppointment}
        >
          Book appointment
        </Button>
      </Stack>
    </Tile>
  )
}
